import type { ProjectTeamMemberView } from '@modules/professional/type/project-team-member';

interface ProjectTeamRowContentProps {
    /** Ordered, public-only category groups from the route loader. */
    groups: ProjectTeamMemberView[];
}

interface ProjectTeamTile {
    id: number;
    name: string;
    /** The professional profile the member belongs to. */
    professionalName: string;
    thumbnail: string;
}

/**
 * (02) Project Team — the open state of the numbered accordion row. Renders the
 * project's team members as a responsive portrait-tile grid (4-up on desktop,
 * 2-up on mobile), matching the June 2026 Figma redesign. Tiles flow
 * left-to-right with the trailing row left-aligned; each shows the member's
 * grayscale portrait, name and role/job title (at reduced opacity), both
 * ellipsis-truncated. See BC-4602.
 */
export const ProjectTeamRowContent = ({
    groups,
}: ProjectTeamRowContentProps) => {
    const seen = new Set<number>();
    const tiles: ProjectTeamTile[] = [];
    for (const group of groups) {
        for (const member of group.TeamMembers ?? []) {
            if (seen.has(member.ID)) continue;
            seen.add(member.ID);
            tiles.push({
                id: member.ID,
                name: member.Name,
                professionalName: member.ProfessionalName,
                thumbnail: member.Thumbnail,
            });
        }
    }

    if (tiles.length === 0) return null;

    return (
        <div className="grid grid-cols-2 gap-x-[1.152rem] gap-y-[3.84rem] max-md:gap-y-10 max-md:pt-6 max-md:pb-4 md:grid-cols-4 md:gap-x-[1.5rem] md:gap-y-[6.25rem] md:pt-[5rem] md:pb-[5rem]">
            {tiles.map((tile) => (
                <div key={tile.id} className="flex flex-col gap-[0.625rem]">
                    <div className="aspect-[437/524] w-full overflow-hidden rounded-tile opacity-90">
                        {tile.thumbnail ? (
                            <img
                                src={tile.thumbnail}
                                alt={tile.name}
                                loading="lazy"
                                className="h-full w-full object-cover object-bottom grayscale"
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center bg-white/5 text-[2.4rem] text-white/40">
                                {tile.name.charAt(0)}
                            </div>
                        )}
                    </div>
                    <div className="min-w-0 font-sans leading-[1.3] text-white">
                        {/* Name 22px/500 (Figma 2354:55315); the subtitle names
                            the profile the member belongs to — no job title
                            (stakeholder call: name + professional only). */}
                        <p className="m-0 truncate text-[1.375rem] font-medium">
                            {tile.name}
                        </p>
                        {tile.professionalName ? (
                            <p className="m-0 truncate text-[1.056rem] text-white/40">
                                {tile.professionalName}
                            </p>
                        ) : null}
                    </div>
                </div>
            ))}
        </div>
    );
};
